// packages/web/src/lib/fieldStatus.ts — S5a §4: OFFLINE IS THE RESTING
// STATE, NOT AN ERROR.
//
// wf03 draws the rule rather than describing it, because on this surface
// offline is normal: a walk-in cooler, a rooftop, a basement mechanical
// room. The banner it draws counts what is waiting and says they will send
// themselves. No retry button, no red, no apology — because nothing has gone
// wrong.
//
// TWO FACTS, TWO SOURCES, AND ONE OF THEM DOES NOT EXIST YET.
//
//   online   — `navigator.onLine` plus the window's own online/offline
//              events. A real browser fact, read live.
//
//   waiting  — how many captures/writes are sitting on this phone with
//              nowhere to go. S5c BUILT THAT QUEUE (lib/outbox.ts), so this
//              is now a real number read from a real device-local store
//              rather than the honest zero S5a could return.
//
// `pendingSendCount` was the seam S5c fills, and it stayed a function rather
// than a constant precisely so that filling it would be this small. It reads
// the outbox's synchronous count cache — IndexedDB is async and a banner
// renders now — and `useFieldStatus` SUBSCRIBES, so the count falls as the
// queue drains without a reload. When it reaches zero and the tech is back
// online the banner returns null and leaves by itself; TechToday.test.tsx
// walks that transition.
//
//   blocked  — the part of the queue that will NOT send itself: a 4xx the
//              server refused, or a 5xx past the attempt cap. Counted apart
//              from `waiting` because the banner's own words are "they will
//              send themselves", and including a refused entry in that number
//              would make the sentence false.
import { useEffect, useState } from 'preact/hooks';
import { outboxCounts, subscribeOutbox, type OutboxCounts } from './outbox';

/** How many captures are waiting on this device to send — and will send
 *  themselves. Real, and it changes. */
export function pendingSendCount(): number {
  return outboxCounts().waiting;
}

/** navigator.onLine, kept live. Defaults to ONLINE where the browser will
 *  not say (jsdom/happy-dom, an old engine): assuming a person is offline
 *  and telling them so would be its own small lie. */
function readOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}

export interface FieldStatus {
  online: boolean;
  waiting: number;
  blocked: number;
}

export function useFieldStatus(): FieldStatus {
  const [online, setOnline] = useState<boolean>(readOnline);
  const [queue, setQueue] = useState<OutboxCounts>(outboxCounts);

  useEffect(() => {
    const sync = () => setOnline(readOnline());
    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);
    // The events can fire between the first render and this effect; re-read
    // rather than trust the initial snapshot.
    sync();
    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('offline', sync);
    };
  }, []);

  useEffect(() => {
    // Same discipline as `online` above, for the same reason: the queue's
    // first count arrives from IndexedDB asynchronously, and it can land
    // between the first render and this effect. Subscribe, then re-read.
    const unsubscribe = subscribeOutbox(setQueue);
    setQueue(outboxCounts());
    return unsubscribe;
  }, []);

  return { online, waiting: queue.waiting, blocked: queue.blocked };
}
